import { ScrollView, SelectBox, TextBox } from "devextreme-react";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AcceptEmployee2,
  DeleteEmployeeFromList2,
  EditReleaseRange2,
  EmployeesAvailableForAssignment2,
  EmployeesToRental2,
  RentalHistoryEmployees2,
  ShareEmployee2,
  WithdrawEmployee2,
} from "../../api/sharingEmployees2";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import { AcceptEmployeePopup2 } from "./components/AcceptEmployeePopup2";
import { AvailableEmployeesView2 } from "./components/AvailableEmployeesView2";
import { EditReleaseRangePopup2 } from "./components/EditReleaseRangePopup2";
import { EmployeesView2 } from "./components/EmployeesView2";
import { HistoryView2 } from "./components/HistoryView2";
import { ShareEmployeePopup2 } from "./components/ShareEmployeePopup2";
import "./TransferOfEmployees2.scss";
import { AvailableEmployee2, HistoryEmployee2, SharedEmployee2 } from "./types";

type ViewMode = "mine" | "available" | "history";

export const TransferOfEmployees2 = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const [viewMode, setViewMode] = useState<ViewMode>("mine");
  const [employees, setEmployees] = useState<SharedEmployee2[]>([]);
  const [available, setAvailable] = useState<AvailableEmployee2[]>([]);
  const [historyEmployees, setHistoryEmployees] = useState<HistoryEmployee2[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");

  const [shareTarget, setShareTarget] = useState<SharedEmployee2 | null>(null);
  const [acceptTarget, setAcceptTarget] = useState<AvailableEmployee2 | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<SharedEmployee2 | null>(null);

  const isHeadAdministrator =
    user?.roles?.includes(RoleEnum.AdministratorGlowny) ?? false;
  const isCompanyAdministrator =
    user?.roles?.includes(RoleEnum.AdministratorSpolki) ?? false;
  const isHR = user?.roles?.includes(RoleEnum.KadrySpolki) ?? false;

  const effectiveCompanyId = useMemo(() => {
    if (companyId && companyId !== 0) return companyId;
    return user?.companyId || 0;
  }, [companyId, user?.companyId]);

  const canAdminWithdraw = useCallback(
    (row: SharedEmployee2) => {
      if (isHeadAdministrator) return true;
      if ((isCompanyAdministrator || isHR) && row.companyId === user?.companyId)
        return true;
      return false;
    },
    [isHeadAdministrator, isCompanyAdministrator, isHR, user?.companyId],
  );

  const fetchMine = useCallback(async () => {
    const res = await EmployeesToRental2(
      isHeadAdministrator ? effectiveCompanyId : undefined,
    );
    if (res.isOk) setEmployees(res.data || []);
    else notify(`${t("Error:")} ${res.error}`, "error");
  }, [effectiveCompanyId, isHeadAdministrator, t]);

  const fetchAvailable = useCallback(async () => {
    const res = await EmployeesAvailableForAssignment2(
      isHeadAdministrator ? effectiveCompanyId : undefined,
    );
    if (res.isOk) setAvailable(res.data || []);
    else notify(`${t("Error:")} ${res.error}`, "error");
  }, [effectiveCompanyId, isHeadAdministrator, t]);

  const fetchHistory = useCallback(async () => {
    const res = await RentalHistoryEmployees2(
      isHeadAdministrator ? effectiveCompanyId : undefined,
    );
    if (res.isOk) setHistoryEmployees(res.data || []);
    else notify(`${t("Error:")} ${res.error}`, "error");
  }, [effectiveCompanyId, isHeadAdministrator, t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchMine(), fetchAvailable()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchMine, fetchAvailable]);

  useEffect(() => {
    if (viewMode !== "history") return;
    let cancelled = false;
    setLoading(true);
    fetchHistory().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [viewMode, fetchHistory]);

  const refreshAfterAction = useCallback(async () => {
    await Promise.all([fetchMine(), fetchAvailable()]);
  }, [fetchMine, fetchAvailable]);

  const handleShareSubmit = useCallback(
    async ({
      startDate,
      endDate,
      indefinite,
    }: {
      startDate: Date;
      endDate: Date;
      indefinite: boolean;
    }) => {
      if (!shareTarget) return;
      setSubmitting(true);
      const res = await ShareEmployee2(
        shareTarget.companyId,
        shareTarget.employeeId,
        startDate,
        endDate,
        indefinite,
      );
      setSubmitting(false);
      if (res.isOk) {
        notify(t("transferOfEmployees.shareSucces"), "success");
        setShareTarget(null);
        await refreshAfterAction();
      } else {
        notify(`${t("Error:")} ${res.error}`, "error");
      }
    },
    [shareTarget, refreshAfterAction, t],
  );

  const handleAcceptSubmit = useCallback(
    async ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
      if (!acceptTarget) return;
      setSubmitting(true);
      const res = await AcceptEmployee2(
        acceptTarget.companyId,
        acceptTarget.employeeId,
        startDate,
        endDate,
      );
      setSubmitting(false);
      if (res.isOk) {
        notify(t("User accepted."), "success");
        setAcceptTarget(null);
        await refreshAfterAction();
      } else {
        notify(`${t("Error:")} ${res.error}`, "error");
      }
    },
    [acceptTarget, refreshAfterAction, t],
  );

  const handleEditSubmit = useCallback(
    async ({
      startDate,
      endDate,
      indefinite,
    }: {
      startDate: Date;
      endDate: Date;
      indefinite: boolean;
    }) => {
      if (!editTarget) return;
      setSubmitting(true);
      const res = await EditReleaseRange2(
        editTarget.companyId,
        editTarget.employeeId,
        startDate,
        endDate,
        indefinite,
      );
      setSubmitting(false);
      if (res.isOk) {
        notify(t("transferOfEmployees2.editRangeSuccess"), "success");
        setEditTarget(null);
        await refreshAfterAction();
      } else {
        notify(`${t("Error:")} ${res.error}`, "error");
      }
    },
    [editTarget, refreshAfterAction, t],
  );

  const handleRemove = useCallback(
    async (row: SharedEmployee2) => {
      const ok = await confirm(
        t("transferOfEmployees2.confirmRemove"),
        t("transferOfEmployees2.confirmRemoveTitle"),
      );
      if (!ok) return;
      const res = await DeleteEmployeeFromList2(row.companyId, row.employeeId);
      if (res.isOk) {
        notify(t("User removed."), "success");
        await refreshAfterAction();
      } else {
        notify(`${t("Error:")} ${res.error}`, "error");
      }
    },
    [refreshAfterAction, t],
  );

  const handleWithdraw = useCallback(
    async (row: SharedEmployee2) => {
      const ok = await confirm(
        t("transferOfEmployees2.confirmWithdraw"),
        t("transferOfEmployees2.confirmWithdrawTitle"),
      );
      if (!ok) return;
      const res = await WithdrawEmployee2(row.companyId, row.employeeId);
      if (res.isOk) {
        notify(t("User withdrawn."), "success");
        await refreshAfterAction();
      } else {
        notify(`${t("Error:")} ${res.error}`, "error");
      }
    },
    [refreshAfterAction, t],
  );

  const viewOptions = useMemo(
    () => [
      { value: "mine", text: t("transferOfEmployees2.viewMine") },
      { value: "available", text: t("transferOfEmployees2.viewAvailable") },
      { value: "history", text: t("transferOfEmployees2.viewHistory") },
    ],
    [t],
  );

  return (
    <ScrollView useNative>
      <div className={`transfer2-wrapper ${isMobile ? "is-mobile" : ""}`}>
        <header className="transfer2-header">
          <div>
            <div className="mb-2 text-2xl font-bold leading-none">
              {t("transferOfEmployees2.pageTitle")}
            </div>
            <div className="mb-1 text-base">
              {viewMode === "mine" && t("transferOfEmployees2.hintMine")}
              {viewMode === "available" &&
                t("transferOfEmployees2.hintAvailable")}
              {viewMode === "history" && t("transferOfEmployees2.hintHistory")}
            </div>
          </div>
          <SelectBox
            width={isMobile ? "100%" : 280}
            items={viewOptions}
            value={viewMode}
            displayExpr="text"
            valueExpr="value"
            onValueChanged={(e) => setViewMode(e.value as ViewMode)}
            label={t("view")}
            labelMode="static"
          />
        </header>

        {isMobile && viewMode !== "history" && (
          <div className="transfer2-search-bar">
            <TextBox
              value={mobileSearch}
              onValueChanged={(e) => setMobileSearch(e.value || "")}
              placeholder={t("transferOfEmployees.Search...")}
              showClearButton
              mode="search"
            />
          </div>
        )}

        {viewMode === "mine" && (
          <EmployeesView2
            employees={employees}
            isMobile={isMobile}
            search={mobileSearch}
            canAdminWithdraw={canAdminWithdraw}
            onShare={(row) => setShareTarget(row)}
            onRemove={handleRemove}
            onEditRange={(row) => setEditTarget(row)}
            onWithdraw={handleWithdraw}
          />
        )}

        {viewMode === "available" && (
          <AvailableEmployeesView2
            employees={available}
            isMobile={isMobile}
            search={mobileSearch}
            onAccept={(row) => setAcceptTarget(row)}
          />
        )}

        {viewMode === "history" && (
          <HistoryView2
            employees={historyEmployees}
            isMobile={isMobile}
            loading={loading}
          />
        )}

        <ShareEmployeePopup2
          visible={!!shareTarget}
          isMobile={isMobile}
          target={
            shareTarget && {
              employeeId: shareTarget.employeeId,
              companyId: shareTarget.companyId,
              name: shareTarget.name,
              surname: shareTarget.surname,
            }
          }
          submitting={submitting}
          onClose={() => setShareTarget(null)}
          onSubmit={handleShareSubmit}
        />

        <AcceptEmployeePopup2
          visible={!!acceptTarget}
          isMobile={isMobile}
          target={acceptTarget}
          submitting={submitting}
          onClose={() => setAcceptTarget(null)}
          onSubmit={handleAcceptSubmit}
        />

        <EditReleaseRangePopup2
          visible={!!editTarget}
          isMobile={isMobile}
          target={editTarget}
          initialStart={editTarget?.releaseStartDate ?? null}
          initialEnd={editTarget?.releaseEndDate ?? null}
          submitting={submitting}
          onClose={() => setEditTarget(null)}
          onSubmit={handleEditSubmit}
        />
      </div>
    </ScrollView>
  );
};

export default TransferOfEmployees2;
